import { idb } from '@/modules/util/idb'

/**
 * Cache module for storing and retrieving data using IndexedDB.
 * 
 * @class Cache
 * @param {string} store - The name of the IndexedDB store.
 * @param {Object} settings - Configuration settings for the cache.
 * @param {string} [keyPath] - The path to the key in the stored data.
 * @param {Function} [keyGen] - Function to generate a key from the value.
 */
export class Cache {
    store
	settings = { 
		expiry: 1000 * 60 * 60 * 24 * 2,
	}

	keyPath
	keyGen

    static idb = idb

    constructor(store, settings={}, keyPath=undefined, keyGen=undefined) {
    	if (keyPath === 'timestamp' || keyPath === 'value') {
    		throw new Error(`Reserved field: '${keyPath}' cannot be used as keyPath`)
    	}

    	this.store = store
    	this.settings = { 
    		...this.settings, 
    		...settings,
    	}
		
    	this.keyPath = keyPath
    	this.keyGen = keyGen
    }

    async get(key) {
    	const data = await idb.get(this.store, key)

    	if (!data) {
    		return null
    	}

    	if (this.settings.expiry) {
    		if (data.timestamp + this.settings.expiry < new Date().getTime()) {
    			idb.remove(this.store, key)
    			return null
    		}
    	}

    	return data
    }

    async all() {
    	await this.clean()
    	return await idb.list(this.store)
    }

    /**
     * Returns a paginated list of items from the cache.
     * 
     * @param {Number} page The page numer, zero-indexed.
     * @param {Number} limit Items per page.
     * @returns {Promise<Array>} - Paginated array of results.
     */
    async list(page=0, limit=100) {
    	await this.clean()
    	return await idb.list(this.store, page, limit)
    }

    async find(cond, keys=false) {
    	await this.clean()
    	const result = []

    	const cb = keys ? (cursor) => {
    		if (cond(cursor.value)) {
    			result.push(cursor.key)
    		}
    	} : (cursor) => {
    		if (cond(cursor.value)) {
    			result.push(cursor.value)
    		}
    	}

    	await idb.each(this.store, cb)
    	return result
    }

    async keys() {
    	await this.clean()
    	return await idb.keys(this.store)
    }

    async set(value, key=undefined) {
    	const data = {
    		timestamp: new Date().getTime(),
    		value,
    	}

    	if (this.keyPath) {
    		if (!key) {
    			if (!this.keyGen) {
    				throw new Error('Key is required, no key generator provided')	
    			}

    			key = this.keyGen(value)
    		}

    		data[this.keyPath] = key
    	}

    	await idb.upsert(this.store, data)
    	return key
    }

    async remove(key) {
    	if (Array.isArray(key)) {
    		if (key.length === 0)
    			return
            
    		const set = new Set(key)

    		return await idb.each(this.store, (cursor) => {
    			if (set.has(cursor.key)) {
    				idb.remove(this.store, cursor.key)
    			}
    		})
    	}
        
    	return await idb.remove(this.store, key)
    }

    async each(cb) {
    	if (this.settings.expiry) {
    		cb = (cursor) => {
    			if (cursor.value.timestamp + this.settings.expiry < new Date().getTime()) {
    				cursor.delete()
    			} else {
    				cb(cursor)
    			}
    		}
    	}

    	return await idb.each(this.store, cb)
    }

    async clean() {
    	if (this.settings.expiry) {
    		await idb.each(this.store, (cursor) => {
    			if (cursor.value.timestamp + this.settings.expiry < new Date().getTime()) {
    				cursor.delete()
    			}
    		})
    	}
    }

    static async clearAll() {
    	return await idb.clearAll()
    }
}

